import { CSSProperties, useEffect, useMemo, useState } from 'react';
import { useMe } from '../../../lib/me';
import { useTranslation } from '../../../lib/i18n';
import { browserIsMonthFirst } from '../../../lib/date-locale';
import { useViewportNarrow } from '../../../lib/use-narrow';
import { greetingLeadsWithName, greetingWithoutName } from '../../../lib/greeting-without-name';

// `compact` is the mobile-home variant (Figma 6377-22668): a small 16/16 greeting beside the big date.
// `part` splits the two apart so a header can place the greeting and the date in different corners. The
// default `full` keeps them together.
export function GreetingDate({
  compact = false,
  part = 'full',
  overview = false,
}: { compact?: boolean; part?: 'full' | 'greeting' | 'date'; overview?: boolean } = {}) {
  const me = useMe();
  const { t } = useTranslation();
  // Read synchronously so a mobile load never first paints the desktop date size and then shifts; the only narrow
  // surfaces that render `part="date"` are client-only, so there is no SSR/hydration tree for the sync read to mismatch.
  const narrow = useViewportNarrow();
  const [now, setNow] = useState(() => new Date());
  useEffect(() => setNow(new Date()), []);

  const displayName = me?.recipientName?.trim() || me?.firstName?.trim() || me?.email?.trim().split('@')[0] || '';
  const name = displayName.split(/\s+/)[0] ?? '';
  const hour = now.getHours();
  const greetingKey = hour < 12 ? 'home.greeting.morning' : hour < 18 ? 'home.greeting.afternoon' : 'home.greeting.evening';
  const greetingRaw = t(greetingKey, { firstName: name });
  // The copy bakes in `, {firstName}`; when we have no name (still loading, or an account with none),
  // drop the dangling separator rather than render "Good morning, ". A locale that puts the name FIRST
  // (Japanese: `{firstName}さん、おはようございます`) has nothing trailing to drop, so it loses the whole
  // name clause instead, or it would greet nobody as 「さん、」.
  const greeting = name
    ? greetingRaw
    : greetingLeadsWithName(t, greetingKey)
      ? greetingWithoutName(t, greetingKey)
      : greetingRaw.replace(/[,，、]\s*$/u, '').trimEnd();
  const day = String(now.getDate()).padStart(2, '0');
  const month = String(now.getMonth() + 1).padStart(2, '0');
  // The date order follows the BROWSER — its language, corrected by its time zone (an `en-US` browser in
  // Israel reads day-first, as an English phone with the Israel region does). Never the account's
  // `siteCountry`: that made a US-region account read month-first for EVERY browser language (a machine set
  // to Hebrew still showed `08.31`). Native keeps the account-region override only for the Android Auto
  // car, which has no user locale to read. See `lib/date-locale.ts`.
  const monthFirst = useMemo(() => browserIsMonthFirst(navigatorLanguage(), browserTimeZone(), now), [now]);
  // The header date is the two-line stacked "MM." / "DD" dot form (Figma node 21639-660649), in locale
  // order — month-first → "MM."/"DD", day-first → "DD."/"MM". The trailing "." is always on the TOP line.
  const [dateFirst, dateSecond] = monthFirst ? [month, day] : [day, month];

  // Both text nodes carry `suppressHydrationWarning` on their DIRECT parent — React only consults it
  // there. The date differs across passes by both its value (`now`) and its ORDER (the browser locale;
  // `navigator` is undefined on the server, so month-vs-day order can only be resolved on the client);
  // the greeting can flip morning/afternoon on the hour boundary between the server render and hydration.
  // Desktop's split greeting sits alone on the top-left, so it takes its natural width; the mobile
  // variants wrap tight (`min-content`) to sit beside the big date.
  const greetingStyle = compact ? styles.greetingCompact : part === 'greeting' ? styles.greetingWide : styles.greeting;
  const greetingNode = (
    <div suppressHydrationWarning style={greetingStyle}>
      {greeting}
    </div>
  );
  // Each line is an ISOLATED LTR run. A numeric date reads left-to-right in every language — Hebrew and
  // Arabic write 08.11 exactly as English does — but the trailing "." is bidi-NEUTRAL, so in an RTL
  // document it takes the paragraph's own direction and lands to the LEFT of the digits: the Hebrew home
  // rendered ".11" over "08" instead of "11." over "08". `dir="ltr"` pins the separator after its number,
  // in every RTL locale at once. This is our own generated numeric run, not localized copy — so unlike
  // Golden Rule 13's user/LLM text it does not follow the app language at all.
  //
  // The isolate sits on the INNER span, never on the line box: the box keeps the document's direction, so
  // `textAlign: 'end'` still MIRRORS (the stack flushes right in LTR, left in RTL) — that part was always
  // correct and must not flip. `suppressHydrationWarning` moves with the text onto its new direct parent,
  // which is the only element React consults.
  const dateNode = (
    <div aria-hidden style={part === 'date' ? (narrow ? styles.dateNarrow : overview ? styles.dateOverview : styles.dateDesktop) : styles.date}>
      <div>
        <span suppressHydrationWarning dir="ltr" style={styles.dateLine}>{dateFirst}.</span>
      </div>
      <div>
        <span suppressHydrationWarning dir="ltr" style={styles.dateLine}>{dateSecond}</span>
      </div>
    </div>
  );

  if (part === 'greeting') return <div style={styles.type}>{greetingNode}</div>;
  if (part === 'date') return <div style={styles.type}>{dateNode}</div>;

  return (
    <div style={compact ? { ...styles.root, gap: 12 } : styles.root}>
      {greetingNode}
      {dateNode}
    </div>
  );
}

// `undefined` on the server, which is what `Intl` wants for "the runtime default". The two date lines
// carry `suppressHydrationWarning` for the mismatch that follows.
function navigatorLanguage(): string | undefined {
  return typeof navigator === 'undefined' ? undefined : navigator.language;
}

// The browser's clock zone, the region signal `browserIsMonthFirst` reads. `undefined` on the server, so
// the server render keeps the language's answer and the client corrects it under the same
// `suppressHydrationWarning`.
function browserTimeZone(): string | undefined {
  if (typeof navigator === 'undefined') return undefined;
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone;
  } catch {
    return undefined;
  }
}

const type: CSSProperties = {
  fontFamily: 'var(--brand-font-display)',
  fontWeight: 'var(--sym-weight-semibold)',
  textTransform: 'capitalize',
  color: 'rgb(var(--ink-rgb))',
  fontVariationSettings: '"wdth" 100',
  fontFeatureSettings: '"dlig" 1',
};

const styles: Record<string, CSSProperties> = {
  type,
  // One date line's numeric run. `unicode-bidi: isolate` alongside the element's own `dir="ltr"` keeps
  // the run a self-contained LTR island: the digits and their trailing "." order among THEMSELVES, and
  // the island as a whole still sits wherever the surrounding RTL/LTR line puts it.
  dateLine: { unicodeBidi: 'isolate' },
  root: { ...type, display: 'flex', alignItems: 'flex-start', gap: 12, flexShrink: 0, marginInlineStart: 'auto' },
  greeting: { width: 'min-content', textAlign: 'start', fontSize: 32, lineHeight: '34px', letterSpacing: '-0.02em' },
  greetingWide: { textAlign: 'start', fontSize: 32, lineHeight: '34px', letterSpacing: '-0.02em' },
  greetingCompact: { width: 'min-content', textAlign: 'start', fontSize: 16, lineHeight: '16px', letterSpacing: '-0.02em' },
  date: {
    display: 'flex',
    flexDirection: 'column',
    textAlign: 'end',
    fontSize: 32,
    lineHeight: '26px',
    letterSpacing: '-0.07em', // em-relative, so it scales with the size (= -2.24px at 32)
  },
  dateDesktop: {
    display: 'flex',
    flexDirection: 'column',
    textAlign: 'end',
    fontSize: 36,
    lineHeight: '32px',
    letterSpacing: '-0.07em',
  },
  // The OVERVIEW (Home3) desktop date (Figma 21639-650575): Archivo SemiBold 32/26, -7%. One step smaller
  // than the catch-up/map `dateDesktop` above — this frame pairs it with the small 16/16 greeting, so the
  // date leads at 32 rather than 36. Passed only by `Home3Body` (`<GreetingDate part="date" overview />`).
  dateOverview: {
    display: 'flex',
    flexDirection: 'column',
    textAlign: 'end',
    fontSize: 32,
    lineHeight: '26px',
    letterSpacing: '-0.07em',
  },
  // The narrow (mobile-web) `part="date"` — the Overview/new home. Matches the native activity-map date
  // (`topBarDateCompact`, Figma node 21639-660649): Archivo SemiBold 32 / 26, -7% (= -2.24px at 32) — the
  // same compact stacked date as the catch-up mobile `styles.date`.
  dateNarrow: {
    display: 'flex',
    flexDirection: 'column',
    textAlign: 'end',
    fontSize: 32,
    lineHeight: '26px',
    letterSpacing: '-0.07em',
  },
};
